import React, { useState, useContext } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { TextField } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'

// components
import Phone from '../components/fields/Phone'

// utils
import { getBaseUrl } from '../utils'
import { validateFields } from '../utils/validation'

// layout
import Layout from '../layout'

// styles
import { useStyles } from '../styles/common.style'

// store
import { Context } from '../store'
import types from '../store/types'

// API
import { userSignedAgreement } from '../API'

import { t } from 'application-translator/dist/translate/translate.react.js';

function EnterDetails() {
  const classes = useStyles()

  // global state
  const { globalState, dispatch } = useContext(Context)
  const { signedBy, signedEmail, phone, agreed, countryCode } =
    globalState.userData || {}
  const clubId = globalState.clubData.id

  // router hooks
  const location = useLocation()
  const history = useHistory()

  // local state
  const [validationMessages, setValidationMessages] = useState({
    signedBy: '',
    signedEmail: '',
    phone: '',
    agreed: '',
  })
  const [validationErrors, setValidationErrors] = useState({
    signedBy: false,
    signedEmail: false,
    phone: false,
    agreed: false,
  })

  const fields = Object.keys(validationErrors)
  const hasEmptyValue = () => !signedBy || !signedEmail || !phone || !agreed
  const hasValidationError = fields.some((key) => validationErrors[key])

  const updateFormData = (field, value, fieldType) => {
    const data = {}
    data[field] = value

    const { error, message } = validateFields(fieldType, value)
    const result = {
      errors: validationErrors,
      messages: validationMessages,
    }

    // set error
    result.errors[field] = error
    setValidationErrors(result.errors)

    // set messages
    result.messages[field] = message
    setValidationMessages(result.messages)

    // update store
    dispatch({ type: types.UPDATE_USER_DATA, payload: data })
  }

  // callbacks
  const handleChange = ({ target }, fieldType) => {
    let { name: field, value, checked, type } = target
    if (type === 'checkbox') {
      value = checked ? 1 : 0
    }
    updateFormData(field, value, fieldType)
  }

  const handlePhoneCallback = (data) => {
    const { value, error, countryCode: country } = data || {}
    const countryCode = country?.countryCode?.toUpperCase()
    dispatch({
      type: types.UPDATE_USER_DATA,
      payload: { countryCode, phone: value },
    })
    setValidationErrors({
      ...validationErrors,
      phone: error,
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    callApi()
  }

  // api call...
  const callApi = async () => {
    // show loading
    dispatch({ type: types.SET_LOADING, payload: true })

    const res = await userSignedAgreement({
      phone,
      club_id: clubId,
      clubUserAgreementId: globalState.clubUserAgreementId,
      countryCode,
      agreed,
      signedBy,
      signedEmail,
      createBookings: true,
    })

    // redirect to next page after api call
    // @TODO: use result from api
    const result = res && res?.length > 0
    const { pathname } = location
    const baseUrl = getBaseUrl(pathname)

    // show loading
    dispatch({ type: types.SET_LOADING, payload: false })

    // @TODO if result is false show error message
    if (result) {
      history.push(`${baseUrl}/success`)
      return
    }

    history.push(`${baseUrl}/sorry`)
  }

  // show agreement
  const showAgreement = (e) => {
    e.preventDefault()
    dispatch({ type: types.TOGGLE_AGGREEMENT })
  }

  return (
    <Layout title={t('Great!')}>
      <form
        className={classes.content}
        action="#"
        method="POST"
        onSubmit={handleSubmit}
      >
        <p>{t('Please enter your details:')}</p>

        <TextField
          name="signedBy"
          label={t('Name')}
          placeholder={t('Your full name')}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          value={signedBy}
          variant="outlined"
          className={classes.textField}
          onChange={(e) => {
            handleChange(e, 'required')
          }}
          error={validationErrors.signedBy}
          helperText={validationMessages.signedBy}
          autoFocus
        />

        <TextField
          name="signedEmail"
          label={t('Email')}
          placeholder={t('Your email address')}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          value={signedEmail}
          variant="outlined"
          className={classes.textField}
          onChange={(e) => {
            handleChange(e, 'email')
          }}
          error={validationErrors.signedEmail}
          helperText={validationMessages.signedEmail}
        />

        <Phone
          callback={handlePhoneCallback}
          error={validationErrors.phone}
          initValue={phone}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={agreed === 1}
              onChange={(e) => handleChange(e, 'required')}
              name="agreed"
              color="primary"
            />
          }
          label={
            <p>
              {t('I accept the')}{' '}
              <a
                onClick={showAgreement}
                href="/terms-and-conditions"
                className={classes.link}
              >
                {t('terms and conditions')}
              </a>
            </p>
          }
        />

        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={hasValidationError || hasEmptyValue()}
        >
          {t('Submit')}
        </Button>
      </form>
    </Layout>
  )
}

export default EnterDetails
